import { usePublic } from '@app/components/public'
import { type ThemePreference, useTheme } from '@app/lib/theme'
import { brandingUrl } from '@shared/contracts/branding'
import {
  type PublicSettings,
  SETTING_DEFS,
  type SettingKey,
  type SettingValue,
} from '@shared/contracts/settings'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'

/** Public settings are cached client-side; every shell can read them without a second fetch. */
function usePublicSettings() {
  return usePublic<PublicSettings>('settings', '/api/v1/public/settings')
}

/**
 * One public setting. `undefined` while settings load, so callers choose their own loading
 * behaviour; `orDefault` falls back to the registry default instead.
 */
export function useSetting<K extends SettingKey>(key: K): SettingValue<K> | undefined
export function useSetting<K extends SettingKey>(key: K, orDefault: true): SettingValue<K>
export function useSetting<K extends SettingKey>(key: K, orDefault?: boolean) {
  const q = usePublicSettings()
  const v = q.data?.[key] as SettingValue<K> | undefined
  return v === undefined && orDefault ? SETTING_DEFS[key].default : v
}

/** The storefront is hidden until settings confirm it is switched on (no flash of a closed shop). */
export function useShopEnabled() {
  return useSetting('shop.enabled') === true
}

/** Resolved brand asset URLs (null while settings load or when nothing is uploaded). */
function useBranding() {
  const q = usePublicSettings()
  const s = q.data?.['site.branding']
  return {
    loaded: !!q.data,
    logo: s ? brandingUrl('logo', s) : null,
    logoDark: s ? brandingUrl('logo_dark', s) : null,
  }
}

/**
 * The site identity: the uploaded logo when there is one, otherwise the "BP" mark and name.
 * `size` picks the header (regular) or sidebar/receipt (small) height.
 */
export function Brand({
  size = 'regular',
  name = 'BTown Pride',
  showName = false,
}: {
  size?: 'regular' | 'small'
  name?: string
  showName?: boolean
}) {
  const b = useBranding()
  const cls = `brand${size === 'small' ? ' brand--small' : ''}`
  if (b.logo) {
    return (
      <span className={cls}>
        <img
          src={b.logo}
          alt={showName ? '' : name}
          className={`brand__logo${b.logoDark ? ' brand__logo--light' : ''}`}
        />
        {b.logoDark ? (
          <img src={b.logoDark} alt="" className="brand__logo brand__logo--dark" />
        ) : null}
        {showName ? <span>{name}</span> : null}
      </span>
    )
  }
  return (
    <span className={cls}>
      <span className="brand__mark" aria-hidden="true">
        BP
      </span>
      <span>{name}</span>
    </span>
  )
}

/** Announcement strip driven by the `site.banner` setting. */
export function SiteBanner() {
  const q = usePublicSettings()
  const b = q.data?.['site.banner']
  if (!b?.enabled || !b.text) return null
  const inner = b.link ? (
    b.link.startsWith('/') ? (
      <Link to={b.link}>{b.text}</Link>
    ) : (
      <a href={b.link}>{b.text}</a>
    )
  ) : (
    b.text
  )
  return (
    <div className={`site-banner site-banner--${b.tone}`} role="status">
      {inner}
    </div>
  )
}

/** Shows when the browser reports it is offline; cached pages keep working via the service worker. */
export function OfflineBanner() {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])
  if (online) return null
  return (
    <div className="offline-banner" role="status" aria-live="polite">
      You are offline. Saved pages still work; changes will send when you reconnect.
    </div>
  )
}

/** Mailing address from settings, for footers and receipts. */
export function SiteAddress() {
  const q = usePublicSettings()
  const address = q.data?.['site.address']
  if (!address) return null
  return <span>{address}</span>
}

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: string }[] = [
  { value: 'light', label: 'Light', icon: '☀️' },
  { value: 'dark', label: 'Dark', icon: '🌙' },
  { value: 'system', label: 'System', icon: '🖥️' },
]

/** Light / Dark / System switch, remembered on this device. */
export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const [pref, setPref] = useTheme()
  return (
    <fieldset className={`theme-toggle${compact ? ' theme-toggle--compact' : ''}`}>
      <legend className="sr-only">Colour theme</legend>
      {THEME_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`theme-toggle__btn${pref === o.value ? ' is-active' : ''}`}
          aria-pressed={pref === o.value}
          aria-label={`${o.label} theme`}
          title={`${o.label} theme`}
          onClick={() => setPref(o.value)}
        >
          <span aria-hidden="true">{o.icon}</span>
          {compact ? null : <span className="theme-toggle__label">{o.label}</span>}
        </button>
      ))}
    </fieldset>
  )
}

const SOCIAL_LABELS: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
  bluesky: 'Bluesky',
}

/** Simple network glyphs drawn inline (no icon font, no requests), in the current text colour. */
function Glyph({ children, outline = false }: { children: React.ReactNode; outline?: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      fill={outline ? 'none' : 'currentColor'}
      stroke={outline ? 'currentColor' : 'none'}
      strokeWidth="2"
    >
      {children}
    </svg>
  )
}

function SocialIcon({ network }: { network: string }) {
  switch (network) {
    case 'instagram':
      return (
        <Glyph outline>
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" stroke="none" />
        </Glyph>
      )
    case 'facebook':
      return (
        <Glyph>
          <path d="M15.5 3H13a4.5 4.5 0 0 0-4.5 4.5V10H6v4h2.5v7h4v-7h3l.5-4h-3.5V7.8c0-.5.3-.8.8-.8h2.2z" />
        </Glyph>
      )
    case 'tiktok':
      return (
        <Glyph>
          <path d="M16 3c.3 2.3 1.8 3.9 4 4.1v3.4a7 7 0 0 1-4-1.2v6.2A5.5 5.5 0 1 1 10.5 10v3.5a2.1 2.1 0 1 0 2 2.1V3z" />
        </Glyph>
      )
    case 'bluesky':
      return (
        <Glyph>
          <path d="M12 11C10.5 8.1 6.4 2.7 3 4c-1.8.7-.6 6.1.4 7.4 1 1.4 3.3 1.8 5 1.6-2.6.6-5.2 2.4-2.3 5.6 3 3.2 5.2-.6 5.9-3 .7 2.4 2.6 6.2 5.9 3 2.9-3.2.3-5-2.3-5.6 1.7.2 4-.2 5-1.6 1-1.3 2.2-6.7.4-7.4-3.4-1.3-7.5 4.1-9 7z" />
        </Glyph>
      )
    default:
      return (
        <Glyph outline>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
        </Glyph>
      )
  }
}

/** Footer social links from `site.social` as icon buttons; networks left blank are hidden. */
export function SocialLinks() {
  const social = useSetting('site.social')
  const links = Object.entries(social ?? {}).filter(([, url]) => !!url?.trim())
  if (links.length === 0) return null
  return (
    <ul className="social-links" aria-label="BTown Pride on social media">
      {links.map(([key, url]) => (
        <li key={key}>
          <a
            href={/^https?:\/\//.test(url) ? url : `https://${url}`}
            rel="noopener noreferrer"
            aria-label={SOCIAL_LABELS[key] ?? key}
            title={SOCIAL_LABELS[key] ?? key}
          >
            <SocialIcon network={key} />
          </a>
        </li>
      ))}
    </ul>
  )
}

/** Contact email from `site.contact_email` as a mailto link. */
export function ContactEmail({ children }: { children?: React.ReactNode }) {
  const email = useSetting('site.contact_email')
  if (!email) return null
  return <a href={`mailto:${email}`}>{children ?? email}</a>
}
